/**
 * WizardAnswers → CaseFacts mapping. Pure and unit-tested; `asOf` is always
 * passed in so the mapping never reads the clock either — only main.ts, at
 * the boundary, asks the browser for today.
 *
 * The rule this file follows: **an unanswered question maps to `null`, never
 * to a value.** The engine reads null as PENDING_EVIDENCE and never as
 * satisfied (§8.5), and "not sure" is a real answer meaning exactly that.
 */
import type { CaseFacts, ISODate, Stage } from "@verity/engine";
import { addMonthsISO } from "@verity/engine";

/** "FIANCE" is not an engine stage — it routes out (E-ECP.2.8). */
export type WizardStage = Stage | "FIANCE";

export interface WizardAnswers {
  // 1 · the sponsor
  sponsor_status: CaseFacts["sponsor"]["status"];
  sponsor_receives_benefit: boolean | null;

  // 2 · the application
  stage: WizardStage;
  intended_date: ISODate | null;
  first_applied_before_cutoff: boolean | null;
  granted_on_first_application: boolean | null;
  holds_five_year_route_permission: boolean | null;
  applying_with_same_partner: boolean | null;

  // 3 · the relationship
  relationship_type: CaseFacts["relationship"]["type"];
  date_of_marriage: ISODate | null;
  marriage_legally_valid: boolean | null;
  cohabiting_since: ISODate | null;
  both_over_18: boolean | null;
  met_in_person: boolean | null;
  within_prohibited_degrees: boolean | null;
  previous_relationships_ended: boolean | null;

  // 4 · money
  route: "employment" | "savings" | "combination" | "other";
  income_from: CaseFacts["finances"]["income_from"];
  months_with_employer: number | null;
  gross_annual: number | null;
  savings_amount: number | null;
  savings_held_since: ISODate | null;

  // 5 · english
  english_exemption: CaseFacts["english"]["exemption"];
  english_level: CaseFacts["english"]["level_held"];

  // 6 · where you will live
  legal_right_to_occupy: boolean | null;
  without_public_funds: boolean | null;

  // 7 · life in the UK
  life_in_uk_passed: boolean | null;

  // 8 · children
  dependent_children: boolean;
}

/** Any date on or after the cutoff reads as "not transitional". */
const PRE_CUTOFF_DATE: ISODate = "2024-04-10";

/** The wizard's own gates, which short-circuit before any requirement is evaluated. */
export function outOfScopeGate(answers: WizardAnswers): keyof CaseFacts["out_of_scope_flags"] | null {
  if (answers.sponsor_status === "none_of_these") return "sponsor_not_eligible_to_sponsor";
  if (answers.sponsor_receives_benefit === true) return "adequate_maintenance_route";
  if (answers.stage === "FIANCE") return "fiance_or_proposed_civil_partner";
  if (answers.route === "other") return "self_employed_income";
  if (answers.dependent_children) return "dependent_children";
  return null;
}

export function toCaseFacts(answers: WizardAnswers, asOf: ISODate): CaseFacts {
  // FIANCE never reaches the engine — it is gated above — but the type needs a stage.
  const stage: Stage = answers.stage === "FIANCE" ? "EC" : answers.stage;

  const employment =
    answers.route === "savings" || answers.route === "other" || answers.months_with_employer === null
      ? null
      : {
          employer_start: addMonthsISO(asOf, -answers.months_with_employer),
          gross_annual: answers.gross_annual,
          paid_frequency: null,
          same_employer_6_months: answers.months_with_employer >= 6,
        };

  const savings =
    answers.route === "employment" ||
    answers.route === "other" ||
    answers.savings_amount === null ||
    answers.savings_held_since === null
      ? null
      : {
          amount: answers.savings_amount,
          held_since: answers.savings_held_since,
          // Not asked — unknown, not assumed.
          lawful_source: null,
          under_own_control: null,
        };

  let firstApplicationDate: ISODate | null;
  if (stage === "EC") {
    firstApplicationDate = answers.intended_date ?? asOf;
  } else if (answers.first_applied_before_cutoff === null) {
    firstApplicationDate = null;
  } else {
    firstApplicationDate = answers.first_applied_before_cutoff ? PRE_CUTOFF_DATE : asOf;
  }

  const gate = outOfScopeGate(answers);

  return {
    as_of: asOf,
    stage,
    intended_application_date: answers.intended_date,
    first_application_date: firstApplicationDate,
    transitional: {
      granted_on_first_application: answers.granted_on_first_application,
      holds_five_year_route_partner_permission: answers.holds_five_year_route_permission,
      applying_with_same_partner: answers.applying_with_same_partner,
    },
    sponsor: {
      status: answers.sponsor_status,
      receives_qualifying_disability_or_carer_benefit: answers.sponsor_receives_benefit,
    },
    relationship: {
      type: answers.relationship_type,
      date_of_marriage: answers.date_of_marriage,
      cohabiting_since: answers.cohabiting_since,
      met_in_person: answers.met_in_person,
      both_over_18: answers.both_over_18,
      marriage_legally_valid: answers.marriage_legally_valid,
      within_prohibited_degrees: answers.within_prohibited_degrees,
      previous_relationships_ended: answers.previous_relationships_ended,
    },
    finances: {
      route: answers.route === "other" ? "employment" : answers.route,
      income_from: answers.income_from,
      // Established by the question's own wording: "before tax".
      figures_are_gross: true,
      sponsor_employment: employment,
      cash_savings: savings,
    },
    english: { level_held: answers.english_level, exemption: answers.english_exemption, nationality: null },
    accommodation: {
      // Adequacy is a caseworker judgement and is never asked (ADR-0022).
      adequate: null,
      legal_right_to_occupy: answers.legal_right_to_occupy,
      without_public_funds: answers.without_public_funds,
    },
    koll: { life_in_uk_passed: answers.life_in_uk_passed, exempt_age_or_medical: null },
    out_of_scope_flags: {
      dependent_children: answers.dependent_children,
      self_employed_income: answers.route === "other",
      previous_refusal: false,
      overstay: false,
      criminal_record: false,
      ten_year_route: false,
      adequate_maintenance_route: gate === "adequate_maintenance_route",
      fiance_or_proposed_civil_partner: gate === "fiance_or_proposed_civil_partner",
      sponsor_not_eligible_to_sponsor: gate === "sponsor_not_eligible_to_sponsor",
    },
  };
}
